import http from 'http.js';
import {format} from 'date-fns';
import moment from 'moment';
import config from './Mh1config';

/*
 * Types
   BookingIntegration = {
       "name": "MH1",
       "url": "",
       "bookingId": "495"
   }

   AvailabilitySummary = {
      id: "495"
      next_available: "2025-01-27T10:05:00.000+10:00" // ISO8601 Extended 
      slot_id: "117371924"
   }
 */

export const MH1 = 'mh1';

/**
 *
 * @param availabilitySummaries:AvailabilitySummary[]
 * @param bookingIntegration:BookingIntegration
 * @returns availability:AvailabilitySummary
 */
export const getBookingAvailability = (
    availabilitySummaries,
    bookingIntegration,
) => {
    if (!bookingIntegration || !availabilitySummaries?.length) {
        return undefined;
    }

    // check matching availability
    const availability = availabilitySummaries.find(
        (d) => d.id.toString() === bookingIntegration.bookingId.toString(),
    );

    if (!availability || !availability.next_available) {
        return undefined;
    }

    const dt = moment.parseZone(availability.next_available);
    if (!dt.isValid()) {
        return undefined;
    }

    // return with formatted date time (immutable)
    return {
        ...availability,
        nextAvailableTime: dt.format(config.format.long),
        nextAvailableTimeShort: dt.format(config.format.short),
    };
};

// ============================================================
// [TODO:TEMP] This is a temporary solution until we fully switch to the new backend field and remove the old one,
// then we can remove this adapter function and update callers to use the new field directly.
// ============================================================
/**
 * HS-10302: adapt the new backend `bookingAvailability` field into the same
 * shape `getBookingAvailability` returns, so existing callers
 * (AvailabilitySnippet / MH1BookNowBtn) keep working unchanged.
 *
 *
 * @param bookingAvailability:{available:boolean, mh1:{practitionerId, nextAvailable, slotId, newPatientsOnly}|null}|null
 * @returns availability:AvailabilitySummary | undefined
 */
export const getBookingAvailabilityFromApi = (bookingAvailability) => {
    if (!bookingAvailability?.available || !bookingAvailability.mh1) {
        return undefined;
    }
    const {practitionerId, nextAvailable, slotId} = bookingAvailability.mh1;
    if (!nextAvailable) {
        return undefined;
    }
    const dt = moment.parseZone(nextAvailable);
    if (!dt.isValid()) {
        return undefined;
    }
    return {
        id: practitionerId,
        next_available: nextAvailable,
        slot_id: slotId,
        nextAvailableTime: dt.format(config.format.long),
        nextAvailableTimeShort: dt.format(config.format.short),
    };
};

/**
 * HS-10302: pure replacement for HealthFundStore.enabledMH1BookingIntegration.
 * Returns whether the MH1 new-patient-next-available snippet should be shown
 * for this practitioner. Combines the HF / skin / viewer-type gate with the
 * practitioner-side opt-in flag (`newPatientNextAvailableOnly`, set by
 * HS-9280 on the booking integration).
 *
 * The slot-state check (`bookingAvailability.mh1.newPatientsOnly`) is
 * intentionally kept outside — it is BE-driven runtime data, not a
 * display rule, so callers compose it on top.
 *
 * @param {object} args
 * @param {object} [args.bookingIntegration] - practitioner's booking integration; must expose `newPatientNextAvailableOnly`
 * @param {boolean} args.isHealthFundBookingsEnabled
 * @param {boolean} args.hasSkin
 * @param {boolean} args.isClient
 * @param {boolean} args.isLoggedInGP
 * @returns {boolean}
 */
export const canShowMh1NewPatientSnippet = ({
    bookingIntegration,
    isHealthFundBookingsEnabled,
    hasSkin,
    isClient,
    isLoggedInGP,
}) =>
    !!(
        isHealthFundBookingsEnabled &&
        !!bookingIntegration?.newPatientNextAvailableOnly && // possibly undefined
        !isClient &&
        !isLoggedInGP &&
        !hasSkin
    );

export class Mh1BookingApi {
    /**
     * extract bookingIntegrationId list from practices list
     * @param items:Practice[]
     * @param key:string = "mh1"
     * @returns bookingIds:[]
     */
    extractBookingIntegrationIds(items, key = MH1) {
        const bookingIds = [];

        if (items.length === 0) return [];

        items.forEach((item) => {
            if (item?.bookingIntegration?.name?.toLowerCase() === key) {
                bookingIds.push(item.bookingIntegration.bookingId);
            }
        });

        return bookingIds;
    }

    async fetchAvailabilitySummary(items, opts = {newPatientsOnly: false}) {
        if (!items.length) {
            return [];
        }
        return await this.callAvailabilitySummary(items, opts.newPatientsOnly);
    }

    /**
     * Call Mh1 API
     * @param practitionerIds: string[]
     * @returns availabilitySummary: AvailabilitySummary[]
     */
    async callAvailabilitySummary(practitionerIds, newPatientsOnly = false) {
        // those values are set in feature flags (MH1_URLS)
        const mh1Url = document.body?.dataset?.mh1AvailabilityUrl;
        const mh1NoOfDays = Number(document.body?.dataset?.mh1NoOfDays);

        try {
            if (mh1Url === undefined) {
                throw `Availability API not defined`;
            }

            const response = await http.get({
                url: `${mh1Url}/${config.endpoints.availabilitySummary}`,
                headers: config.headers,
                data: {
                    practitionerIds: practitionerIds,
                    newPatientsOnly,
                    startDate: format(new Date(), config.format.isoDate),
                    noOfDays: mh1NoOfDays,
                },
            });

            let availabilitySummary = [];
            if (response?.data?.availability?.practitioners?.length) {
                availabilitySummary =
                    response.data.availability?.practitioners;
            }

            return availabilitySummary;
        } catch (error) {
            throw `${mh1Url} is not active`;
        }
    }
}
